'use client';

/**
 * Ambient QR handoff lifecycle for the top-level Scribe draft (LF-3835).
 *
 * The top-level analog of the matter `MobileHandoffPanel`'s internal wiring,
 * but ambient and matterless. It owns:
 *
 *  - the handoff URL + expiry, held in **local state** from the `regenerate`
 *    response (never on the recording detail — Option 3, see fe-refactor-guide §3);
 *  - the **mint-on-mount** (and mint-on-return-to-idle) behaviour;
 *  - the **Refresh** affordance;
 *  - the **409 already-claimed** race (phone took over before/while minting).
 *
 * The handoff *phase* and *claimed* timestamps come from the polled
 * `useRecordingDetail` in the caller — this hook does not poll. It only owns the
 * URL/expiry and the claimed-elsewhere short-circuit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { DirectApiError } from '@/lib/api/direct-client';
import { useRegenerateHandoff } from './hooks';

/**
 * True when a thrown error is the BE's 409 `HANDOFF_ALREADY_CLAIMED` — a phone
 * already claimed the handoff (e.g. a reload after a scan).
 *
 * Keys on the exact structured `code`, **not** a bare 409: the BE's pre-capture
 * guard returns a *code-less* 409 when `regenerate` is called on a
 * `processing`/`ready`/`imported` row, which is a different condition. The
 * ambient flow only mints on a `draft`/idle row so it can't hit that guard, but
 * we stay correct by matching on the code rather than the status.
 */
export function isHandoffAlreadyClaimed(error: unknown): boolean {
  return (
    error instanceof DirectApiError &&
    error.response?.code === 'HANDOFF_ALREADY_CLAIMED'
  );
}

export interface UseScribeHandoffResult {
  /** Handoff URL to encode as the QR. Null until the first mint resolves. */
  handoffUrl: string | null;
  /** ISO 2-min scan deadline, from the same `regenerate` response as the URL. */
  handoffExpiresAt: string | null;
  /** Re-mint the token (Refresh / countdown-expired). */
  refresh: () => void;
  /** A mint/refresh is in flight. */
  isRefreshing: boolean;
  /** A phone claimed the handoff — caller should show the in-flight view. */
  claimedElsewhere: boolean;
}

export function useScribeHandoff(
  recordingId: number,
  opts: { enabled: boolean }
): UseScribeHandoffResult {
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const [handoffExpiresAt, setHandoffExpiresAt] = useState<string | null>(null);
  const [claimedElsewhere, setClaimedElsewhere] = useState(false);
  // In-flight is tracked here, NOT via `regenerate.isPending` (LF-4154). The
  // mint is fired fire-and-forget on mount; under React StrictMode the mutation
  // observer gets orphaned and its `isPending` sticks at `true` forever, which
  // left the post-expiry "Regenerate QR" CTA permanently disabled + spinning.
  // This hook already owns the whole mint lifecycle, so it owns the flag too.
  const [isRefreshing, setIsRefreshing] = useState(false);

  // matterId omitted → the orphan (matterless) regenerate path.
  const regenerate = useRegenerateHandoff(recordingId);

  // Keep the latest mutation reachable from a stable `mint` callback without
  // re-creating it each render (mutateAsync identity is stable, but this also
  // keeps the effect deps clean).
  const regenerateRef = useRef(regenerate);
  regenerateRef.current = regenerate;
  const mintInFlightRef = useRef(false);

  const mint = useCallback(async () => {
    if (mintInFlightRef.current) return;
    mintInFlightRef.current = true;
    setIsRefreshing(true);
    try {
      const res = await regenerateRef.current.mutateAsync();
      setHandoffUrl(res.handoffUrl);
      setHandoffExpiresAt(res.handoffExpiresAt);
      setClaimedElsewhere(false);
    } catch (error) {
      if (isHandoffAlreadyClaimed(error)) {
        // A phone already claimed (e.g. reload after a scan). Short-circuit to
        // the in-flight view; the polled detail confirms handoffPhase next tick.
        setClaimedElsewhere(true);
      }
      // Any other failure (network / 5xx): leave handoffUrl null so the card
      // shows its retry affordance. Deliberately not rethrown — `mint` is fired
      // as a fire-and-forget (`void mint()`), and an unhandled rejection would
      // surface as a console error with no UX benefit. `regenerate.isError`
      // remains available if the caller wants to react.
    } finally {
      mintInFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, []);

  // Mint on the `enabled` false→true transition (not merely "once per mount"):
  // a fresh mount (reload) AND a return to draft-idle after a discarded desktop
  // capture both re-mint. A plain mount guard would leave a dead/expired QR in
  // the second case.
  const wasEnabledRef = useRef(false);
  useEffect(() => {
    if (opts.enabled && !wasEnabledRef.current) {
      void mint();
    }
    wasEnabledRef.current = opts.enabled;
  }, [opts.enabled, mint]);

  return {
    handoffUrl,
    handoffExpiresAt,
    refresh: () => void mint(),
    isRefreshing,
    claimedElsewhere
  };
}
